import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const VANTAGGI = [
  { titolo: "Sviluppo delle Competenze Professionali", desc: "I giovani apprendisti acquisiscono competenze pratiche e teoriche che rispondono alle reali esigenze aziendali, garantendo una crescita professionale mirata." },
  { titolo: "Integrazione Ottimale nel Settore", desc: "Le aziende possono formare i propri apprendisti in modo personalizzato, adattando il percorso formativo alle specifiche necessità del settore." },
  { titolo: "Sostegno alla Crescita Aziendale", desc: "Investire nei giovani talenti permette di rafforzare il team con collaboratori altamente qualificati e pronti a contribuire attivamente alla crescita dell'impresa." },
  { titolo: "Accesso a Finanziamenti e Agevolazioni", desc: "Confcommercio Roma aiuta le aziende ad accedere ai finanziamenti per la formazione di apprendisti, riducendo il carico finanziario." },
];

const STEPS = [
  { n: "1", titolo: "Consulenza Personalizzata", desc: "Consulenza mirata per avviare i contratti di apprendistato, individuare i profili formativi e selezionare i corsi più adatti." },
  { n: "2", titolo: "Pianificazione del Percorso Formativo", desc: "In collaborazione con Promo.Ter srl, progettiamo un percorso che combina l'apprendimento in aula con l'esperienza lavorativa." },
  { n: "3", titolo: "Accesso a Finanziamenti e Agevolazioni", desc: "Guidiamo le aziende nell'accesso a finanziamenti dedicati per supportare la formazione degli apprendisti, minimizzando i costi." },
  { n: "4", titolo: "Monitoraggio e Supporto", desc: "Seguiamo l'andamento dei corsi e forniamo supporto continuo durante l'intero percorso formativo." },
  { n: "5", titolo: "Certificazione delle Competenze", desc: "I giovani lavoratori ottengono una certificazione che attesta la loro preparazione per affrontare nuove sfide professionali." },
];

const PERCHE = [
  "Affiancamento completo: dalla formalizzazione dei contratti alla rendicontazione finale",
  "Percorsi su misura adattati alle specifiche necessità dell'azienda",
  "Vantaggi contributivi e incentivi economici che riducono il costo del lavoro",
  "Inserimento rapido dei giovani talenti, riducendo i tempi di adattamento",
];

export default function ApprendistatoPage() {
  return (
    <main id="main-content" data-testid="apprendistato-page">
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920&q=60&auto=format" alt="Formazione apprendistato" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">Formazione</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Contratto di Apprendistato</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Apprendistato: Investire nel Talento Giovane con Promo.Ter e Confcommercio Roma.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            La <strong className="text-[var(--confcom-text-primary)]">formazione in apprendistato</strong> è un'opportunità pensata per le aziende che desiderano investire nei giovani talenti, offrendo loro una solida base di competenze pratiche, specifiche e funzionali alle esigenze del settore. Questo contratto rappresenta un perfetto equilibrio tra l'apprendimento teorico e l'esperienza sul campo.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Promo.Ter e Confcommercio Roma supportano le aziende nella gestione dei percorsi di formazione apprendistato, assicurando che le imprese possano integrare i nuovi collaboratori in modo efficace, in linea con le esigenze del mercato del lavoro e le normative vigenti.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Vantaggi per le Aziende</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {VANTAGGI.map((v) => (
              <div key={v.titolo} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{v.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Come Funziona</h2>
              <div className="space-y-10">
                {STEPS.map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="w-8 h-8 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed']">{s.n}</div>
                    <div>
                      <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">{s.titolo}</h3>
                      <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[var(--confcom-bg-subtle)] rounded-2xl p-8 border border-[var(--confcom-border)]">
              <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">Perché scegliere Promo.Ter?</h3>
              <div className="space-y-3">
                {PERCHE.map((p) => (
                  <div key={p} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-2">Vuoi attivare subito il tuo percorso formativo o accedere a nuove opportunità? Contattaci!</p>
              <div className="text-white/75 text-sm space-y-1">
                <p>
                  <a href="mailto:commerciale.promoter@confcommercioroma.it" className="hover:text-white transition-colors">commerciale.promoter@confcommercioroma.it</a>
                  {" · "}
                  <a href="mailto:areagestione@confcommercioroma.it" className="hover:text-white transition-colors">areagestione@confcommercioroma.it</a>
                </p>
                <p>
                  <a href="tel:+390668437606" className="hover:text-white transition-colors">0668437 606</a>
                  {" · "}
                  <a href="tel:+390668437530" className="hover:text-white transition-colors">0668437 530</a>
                  {" · "}
                  <a href="tel:+390668437311" className="hover:text-white transition-colors">0668437 311</a>
                </p>
              </div>
            </div>
            <a href="https://forms.gle/98CnoTiUAdGHJ9e88" target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
              Richiedi maggiori informazioni <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
